import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formsAPI } from '../../utils/crmApi';
import { taskAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b last:border-b-0">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="col-span-2 text-sm text-foreground break-words">{value}</div>
  </div>
);

const FormResponseDetail = () => {
  const { id, submissionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await formsAPI.getSubmissions(id);
        const sub = (data.data || []).find(s => s._id === submissionId);
        setSubmission(sub || null);
      } catch (e) {
        setSubmission(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, submissionId]);

  const answerRows = useMemo(() => {
    if (!submission?.answers) return [];
    return Object.entries(submission.answers).map(([k, v]) => ({ key: k, value: typeof v === 'object' ? JSON.stringify(v) : String(v) }));
  }, [submission]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (!submission) return <div className="p-6">Submission not found</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Submission</h1>
        <button className="px-3 py-2 border rounded" onClick={() => navigate(`/forms/${id}/responses`)}>Back to responses</button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-card border rounded p-4">
          <h2 className="text-sm font-medium text-foreground mb-2">Answers</h2>
          <div className="divide-y">
            {answerRows.map(r => (
              <Row key={r.key} label={r.key} value={r.value} />
            ))}
            {answerRows.length === 0 && <div className="text-sm text-muted-foreground">No answers.</div>}
          </div>
        </div>
        <div className="bg-card border rounded p-4">
          <h2 className="text-sm font-medium text-foreground mb-2">Meta</h2>
          <div className="space-y-1 text-sm text-foreground">
            <div>Created: {new Date(submission.createdAt).toLocaleString()}</div>
            <div>Status: {submission.status}</div>
            <div>Duration: {submission.meta?.durationSec ? `${submission.meta.durationSec}s` : '-'}</div>
            <div>Source: {submission.meta?.source || '-'}</div>
          </div>
          <div className="mt-3 flex gap-2">
            <button className="px-2 py-1 border rounded" onClick={() => toast.success('Marked reviewed (placeholder)')}>Mark reviewed</button>
            {submission.clientId && (
              <button className="px-2 py-1 border rounded" onClick={() => navigate(`/clients/${submission.clientId}`)}>Open client</button>
            )}
            <button className="px-2 py-1 border rounded disabled:opacity-50" disabled={creatingTask} onClick={async () => {
              try {
                setCreatingTask(true);
                await taskAPI.create({ title: `Follow-up: ${submission._id}`, description: 'Review submission and take next step', priority: 'medium' });
                toast.success('Task created');
              } catch (e) {
                toast.error('Failed to create task');
              } finally {
                setCreatingTask(false);
              }
            }}>Create task</button>
          </div>
          {submission.scores && Object.keys(submission.scores).length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-foreground mb-1">Scores</h3>
              <div className="space-y-1 text-sm text-foreground">
                {Object.entries(submission.scores).map(([k, v]) => (
                  <div key={k}>{k}: {v}</div>
                ))}
              </div>
            </div>
          )}
          {submission.files && submission.files.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-foreground mb-1">Files</h3>
              <ul className="list-disc pl-5 text-sm">
                {submission.files.map((f, idx) => (
                  <li key={idx}><a className="text-blue-600 underline" href={f.url} target="_blank" rel="noreferrer">{f.name || f.url}</a> ({f.mime || ''})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormResponseDetail;


